import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useState} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {getTagChildrenIds} from '../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps} from '../../helpers/reselectHelpers';
import {TagBulkActionTypesEnum} from '../../models/api/tagBulkActionModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {HttpResponseError} from '../../services/http/httpError';
import {DeleteTagForbiddenError, deleteTagTask} from '../../services/tasks/deleteTagTask';
import {tagsBulkAction} from '../../store/data/tags/tagBulkActions';

/*
 * Props.
 */

interface DeleteTagDialogContainerProps {
  isInArchivedView?: boolean;
  searchQuery: string | undefined;
  tagsToActOn: ReadonlyArray<TagModel>;
  hasAllSelected: boolean;
  tagsUrl: SettingsTagsUrl | undefined;
  onRequestClose: Handler;
  onDelete: Handler;
}

/*
 * Style.
 */

const StyledDeleteErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
`;

/*
 * Redux.
 */

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  tagsBulkAction,
  deleteTag: deleteTagTask,
}));

interface DeleteTagDialogContainerComponentProps
  extends DeleteTagDialogContainerProps,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const DeleteTagDialogContainerComponent: FC<DeleteTagDialogContainerComponentProps> = (props) => {
  const {isInArchivedView, tagsToActOn, searchQuery, hasAllSelected} = props;
  const singleTag = tagsToActOn.length === 1 ? tagsToActOn[0] : undefined;
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | undefined>();

  const renderTitle = () => {
    if (tagsToActOn.length > 1) {
      return <Trans id="tag-edit-deleteconfirmation-headermany">Delete {tagsToActOn.length} tags</Trans>;
    }

    if (!singleTag) {
      return null;
    }

    if (isInArchivedView) {
      return (
        <Trans id="tag-edit-deleteconfirmation-headersinglearchived">
          Delete &quot;{singleTag?.name}&quot;
        </Trans>
      );
    }

    return (
      <Trans id="tag-edit-deleteconfirmation-headersingle">Delete &quot;{singleTag?.name}&quot; tag</Trans>
    );
  };

  const renderCopy = () => {
    if (singleTag) {
      if (isInArchivedView) {
        return (
          <Trans id="tag-edit-deleteconfirmation-content-singlearchived">
            Permanently deleting this tag will remove it from all conversations. This action cannot be undone.
          </Trans>
        );
      }

      return (
        <Trans id="tag-edit-deleteconfirmation-content-single">
          Permanently deleting this tag will:
          <ul>
            {(singleTag && getTagChildrenIds(singleTag, isInArchivedView) && <li>Delete all subtags</li>) ||
              null}
            <li>Remove it from all conversations</li>
            <li>Remove it from all rules</li>
          </ul>
        </Trans>
      );
    }

    if (tagsToActOn.length > 1) {
      if (isInArchivedView) {
        return (
          <Trans id="tag-edit-deleteconfirmation-content-manyarchived">
            Permanently deleting these tags will remove them from all conversations. This action cannot be
            undone.
          </Trans>
        );
      }

      return (
        <Trans id="tag-edit-deleteconfirmation-content-many">
          Permanently deleting these tags will:
          <ul>
            <li>Delete all subtags</li>
            <li>Remove them from all conversations</li>
            <li>Remove them from all rules</li>
          </ul>
        </Trans>
      );
    }

    return null;
  };

  const deleteSingleTag = () => {
    if (!singleTag) {
      return undefined;
    }

    if (getTagChildrenIds(singleTag, isInArchivedView).length > 0) {
      return props.tagsBulkAction({
        type: TagBulkActionTypesEnum.DELETE,
        namespace: singleTag.namespace,
        selectedTagIds: [singleTag.id],
      });
    }

    return props.deleteTag(singleTag);
  };

  const deleteBulkTags = () => {
    if (!props.tagsUrl) {
      return undefined;
    }

    // If we have everything selected, we only pass the search query as the backend knows we want to do a "true select all"
    if (hasAllSelected) {
      return props.tagsBulkAction({
        type: TagBulkActionTypesEnum.DELETE,
        namespace: props.tagsUrl.namespace,
        searchTerm: searchQuery || '',
        searchOptions: {
          parentTagId: props.tagsUrl.tagId,
          behaviors: [TagBehaviorsEnum.CAN_UPDATE],
          excludeTagIds: _.compact([props.tagsUrl.tagId]),
          isArchived: Boolean(isInArchivedView),
        },
      });
    }

    return props.tagsBulkAction({
      type: TagBulkActionTypesEnum.DELETE,
      namespace: props.tagsUrl.namespace,
      selectedTagIds: tagsToActOn.map((t) => t.id),
    });
  };

  const onOkDeleteClick = async () => {
    try {
      setIsDeleting(true);
      if (singleTag) {
        await deleteSingleTag();
      } else {
        await deleteBulkTags();
      }

      setIsDeleting(false);
      if (props.onDelete) {
        props.onDelete();
      }
    } catch (error) {
      setDeleteError(error);
      setIsDeleting(false);

      // If something other than 403 FORBIDDEN, that wasn't expected, so rethrow.
      if (!(error instanceof HttpResponseError) || error.status !== 403) {
        throw error;
      }
    }
  };

  return (
    <OkCancelDialog
      title={renderTitle()}
      okButtonLabel={<Trans id="tag-edit-deletepermanently">Delete permanently</Trans>}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onRequestClose={props.onRequestClose}
      onOkClick={onOkDeleteClick}
      isOkDisabled={isDeleting}
      isCancelDisabled={isDeleting}
    >
      {renderCopy()}
      {deleteError && renderErrorMessage(deleteError)}
    </OkCancelDialog>
  );
};

export const DeleteTagDialogContainer = connect(null, mapDispatchToProps)(DeleteTagDialogContainerComponent);

function renderErrorMessage(error: Error) {
  // TODO - Improve support for specific error messages. PB-13270
  if (error instanceof DeleteTagForbiddenError) {
    return (
      <StyledDeleteErrorDiv>
        <Trans id="tag-edit-deleteconfirmation-forbiddenerror">Unable to delete tag (Forbidden)</Trans>
      </StyledDeleteErrorDiv>
    );
  }

  return (
    <StyledDeleteErrorDiv>
      <Trans id="tag-edit-deleteconfirmation-unknownerror">Unable to delete tag (Unknown)</Trans>
    </StyledDeleteErrorDiv>
  );
}
